<template>
    <div class="issue-list">
        <div class="issue-list__tabs-header">
            <div class="issue-list__tabs" role="tablist">
                <button type="button" role="tab" class="issue-list__tab"
                    :class="{ 'issue-list__tab--active': isGrouped }" :aria-selected="isGrouped"
                    @click="isGrouped = true">
                    {{ t('proofreadingReviseManuscriptPage.groupView') }}
                </button>
                <button type="button" role="tab" class="issue-list__tab"
                    :class="{ 'issue-list__tab--active': !isGrouped }" :aria-selected="!isGrouped"
                    @click="isGrouped = false">
                    {{ t('proofreadingReviseManuscriptPage.manuscriptOrderView') }}
                </button>
            </div>
            <div class="issue-list__tabs-track" aria-hidden="true">
                <div class="issue-list__tabs-indicator"
                    :style="{ transform: isGrouped ? 'translateX(0)' : 'translateX(100%)' }">
                    <div class="issue-list__tabs-indicator__inner" />
                </div>
            </div>
        </div>
        <div ref="issueListRef" class="issue-list__body">
            <p v-if="!hasIssues" class="issue-list__empty">
                {{ t('proofreadingReviseManuscriptPage.noIssues') }}
            </p>
            <template v-else-if="isGrouped">
                <issue-list-group v-for="(category, index) in issueCategories" :key="category.id"
                    :category-id="category.id" :label="category.label" :issues="category.issues"
                    :active-issue-ids="activeIssueIds" :default-open="index === 0" :index="index"
                    @issue-click="onIssueClick" @issue-apply="onIssueApply" @issue-revert="onIssueRevert"
                    @issue-reject-toggle="onIssueRejectToggle" @issue-hover="onIssueHover" />
            </template>
            <ul v-else class="issue-list__flat-list">
                <issue-item v-for="(entry, index) in flatIssues" :key="entry.issue.id" :issue="entry.issue"
                    :is-active="isIssueActive(entry.issue)" :active-location-ids="activeIssueIds"
                    :category-label="entry.issue.issueTypeLabel" :default-open="index === 1"
                    @click="onIssueClick" @apply="(issue) => onIssueApply({ categoryId: entry.categoryId, issue })"
                    @revert="(issue) => onIssueRevert({ categoryId: entry.categoryId, issue })"
                    @reject-toggle="(issue) => onIssueRejectToggle({ categoryId: entry.categoryId, issue })"
                    @hover="onIssueHover" />
            </ul>
        </div>
    </div>
</template>
<script lang="ts" setup>
import {
    computed,
    defineEmits,
    defineExpose,
    defineProps,
    nextTick,
    ref,
} from 'vue';
import { useI18n } from 'vue-i18n-bridge';
import type {
    ProofreadingIssue,
    ProofreadingIssueCategory,
} from '@/types/proofreading';
import { flattenIssueCategories } from '@/lib/utils/proofreadingTransform';
import {
    scrollIntoView,
    SCROLL_INTO_VIEW_DURATION_MS,
} from '@/lib/utils/proofreadingEditorUtil';
import IssueItem from './IssueItem.vue';
import IssueListGroup from './IssueListGroup.vue';

const props = defineProps<{
    issueCategories: ProofreadingIssueCategory[];
    manuscriptKeys: string[];
}>();

const { t } = useI18n();

const isGrouped = ref(true);
const issueListRef = ref<HTMLDivElement | null>(null);
const activeIssueIds = ref<string[]>([]);
const targetIssueId = ref<string | null>(null);

const ACCORDION_TRANSITION_MS = 200;

const flatIssues = computed(() =>
    flattenIssueCategories(props.issueCategories, props.manuscriptKeys),
);

const hasIssues = computed(() => flatIssues.value.length > 0);

const emit = defineEmits<{
    (event: 'issue-click', payload: { issueId: string; episodeKey: string }): void;
    (event: 'issue-apply', payload: { categoryId: string; issue: ProofreadingIssue }): void;
    (event: 'issue-revert', payload: { categoryId: string; issue: ProofreadingIssue }): void;
    (event: 'issue-reject-toggle', payload: { categoryId: string; issue: ProofreadingIssue }): void;
    (event: 'issue-hover', issue: ProofreadingIssue, isHovered: boolean): void;
}>();

function isIssueActive(issue: ProofreadingIssue): boolean {
    if (targetIssueId.value == null) return true;

    return activeIssueIds.value.some(
        (activeId) =>
            issue.id === activeId || (issue.locations?.some((loc) => loc.id === activeId) ?? false),
    );
}

function scrollToIssueId(issueId: string) {
    nextTick(() => {
        const item = issueListRef.value?.querySelector(`[data-issue-id="${issueId}"]`);

        setTimeout(() => {
            // アコーディオンの開閉アニメーションが終わったら、アイテムをスクロールして中央に表示
            if (item instanceof HTMLElement) {
                scrollIntoView(item, SCROLL_INTO_VIEW_DURATION_MS);
            }
        }, ACCORDION_TRANSITION_MS);
    });
}


function setActiveIssueIds(
    issueIds: string[] | null,
    options: { scroll?: boolean } = {},
) {
    const shouldScroll = options.scroll !== false;

    if (issueIds == null || issueIds.length === 0) {
        activeIssueIds.value = [];
        targetIssueId.value = null;
        return;
    }

    let scrollTargetId: string;

    function areSameIssueIds(a: string[], b: string[]): boolean {
        if (a.length !== b.length) return false;
        return a.every((id, index) => id === b[index]);
    }
    if (areSameIssueIds(issueIds, activeIssueIds.value) && targetIssueId.value != null) {
        const currentIndex = issueIds.indexOf(targetIssueId.value);
        const nextIndex =
            currentIndex < 0 || currentIndex >= issueIds.length - 1 ? 0 : currentIndex + 1;
        scrollTargetId = issueIds[nextIndex];
    } else {
        activeIssueIds.value = issueIds;
        scrollTargetId = issueIds[0]!;
    }

    targetIssueId.value = scrollTargetId;
    if (shouldScroll) {
        scrollToIssueId(scrollTargetId);
    }
}

defineExpose({
    setActiveIssueIds,
});

function getCategoryLabel(categoryId: string): string {
    return props.issueCategories.find((category) => category.id === categoryId)?.label ?? '';
}

function onIssueClick(payload: { issueId: string; episodeKey: string }) {
    emit('issue-click', payload);
}

function onIssueApply(payload: { categoryId: string; issue: ProofreadingIssue }) {
    emit('issue-apply', payload);
}

function onIssueRevert(payload: { categoryId: string; issue: ProofreadingIssue }) {
    emit('issue-revert', payload);
}

function onIssueRejectToggle(payload: { categoryId: string; issue: ProofreadingIssue }) {
    emit('issue-reject-toggle', payload);
}

function onIssueHover(issue: ProofreadingIssue, isHovered: boolean) {
    emit('issue-hover', issue, isHovered);
}
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";

.issue-list {
    flex: 0 0 300px;
    min-height: 0;
    padding: 12px 0;
    display: flex;
    flex-direction: column;
    gap: 0;
    background-color: var(--color-background-normal);

    &__tabs-header {
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        gap: 0;
        padding-right: 12px;
    }

    &__tabs {
        display: flex;
    }

    &__tabs-track {
        position: relative;
        height: 4px;
        overflow: hidden;
        background-color: var(--color-surface-muted);
        border-radius: var(--radius-rounded);
    }

    &__tabs-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 50%;
        height: 100%;
        transition: transform 0.15s ease;

        &__inner {
            width: calc(100% - 40px);
            height: 100%;
            background-color: var(--color-primary);
            border-radius: var(--radius-rounded);
            margin-inline: 20px;
        }
    }

    &__tab {
        flex: 1;
        padding: 8px 0;
        border: none;
        background: transparent;
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-regular);
        line-height: var(--line-height-default);
        color: var(--color-text-low);
        cursor: pointer;
        transition: color 0.15s ease;

        &--active {
            font-weight: var(--font-weight-bold);
            color: var(--color-text-normal);
        }
    }

    &__body {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 0px;
        padding: 0px 12px 0 0;
    }

    &__flat-list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__empty {
        margin: 32px 0;
        text-align: center;
        font-size: var(--font-size-sm);
        color: var(--color-text-low);
    }
}
</style>
